import React from "react"

import { makeStyles } from "makeStyles"

import LockingSystemRow from "./LockingSystemRow"
import LockingSystemKey from "./LockingSystemKey"
import LockingSystemMatrixHeader from "./LockingSystemMatrixHeader"
import LockingSystemMatrixToolbar from "./LockingSystemMatrixToolbar"
import NoCSSGridSupportWarning from "../NoCSSGridSupportWarning"

import {
	LockingSystemReducerState,
	MoveDirection,
	SpecialMatrixType,
} from "reducers/lockingSystemReducer"
import { LockingSystemPreviewProduct } from "@secureo/common/typings/LockingSystem"
import { LockingSystemCategory } from "@secureo/common/typings/Category"

export type GridTemplateColumnName =
	| "position"
	| "actions"
	| "doorName"
	| "lockingComponent"
	| "outerLength"
	| "innerLength"
	| "quantity"
	| "addKeyComponent"
	| string

export type GridTemplateRowName = "matrixHeader" | "addLockingComponent" | string

const useStyles = makeStyles<{
	numberOfKeyComponents: number
	numberOfLockingComponents: number
}>()((_theme, { numberOfKeyComponents, numberOfLockingComponents }) => ({
	grid: {
		display: "grid",
		gridTemplateColumns: `[edit] 50px [position] 60px [actions] 90px [doorName] 150px [lockingComponent] 200px [outerLength] 110px [innerLength] 110px [selectAllRow] 25px repeat(${numberOfKeyComponents}, [keyComponent] 75px)`,
		gridTemplateRows: `150px 160px [matrixHeader] 42px repeat(${numberOfLockingComponents}, [lockingComponent] 75px )`,
		gridGap: "0rem",
		marginTop: "2rem",
	},
}))

const LockingSystemMatrix = ({
	isFrozen = true,
	isLoading,
	lockingSystemCategory,
	matrix,
	matrixType,
	lockingComponentsUuids,
	keyComponentsUuids,
	componentsByUuid,
	charges,
	availableLockingComponents,
	lockingSystemId,
	locale,
	addDefaultLockingComponent,
	addDefaultKeyComponent,
	setCellValue,
	removeLock,
	removeKey,
	duplicateLock,
	duplicateKey,
	setComponentName,
	setComponentQuantity,
	setComponentLockingSystemProduct,
	setComponentOuterLength,
	setComponentInnerLength,
	setEditComponentModalUuid,
	moveMatrixComponent,
	setSpecialMatrix,
	saveLockingSystem,
	duplicateLockingSystem,
	toggleShareModalIsOpen,
	resetLockingSystem,
	toggleLockingComponentSelections,
	toggleKeyComponentSelection,
}: Props) => {
	const { lockingSystemType } = lockingSystemCategory

	const { classes } = useStyles({
		numberOfKeyComponents: keyComponentsUuids.length,
		numberOfLockingComponents: lockingComponentsUuids.length,
	})

	const showKeyColumns = lockingSystemType === "mechanic"

	const { numberOfFreeIncludedKeys, byUuid: chargesByUuid } = charges

	return (
		<>
			<NoCSSGridSupportWarning />

			<div className={classes.grid}>
				<LockingSystemMatrixToolbar
					isFrozen={isFrozen}
					isLoading={isLoading}
					matrixType={matrixType}
					numberOfFreeIncludedKeys={numberOfFreeIncludedKeys}
					showKeyColumns={showKeyColumns}
					addDefaultLockingComponent={addDefaultLockingComponent}
					addDefaultKeyComponent={addDefaultKeyComponent}
					setSpecialMatrix={setSpecialMatrix}
					saveLockingSystem={saveLockingSystem}
					duplicateLockingSystem={duplicateLockingSystem}
					toggleShareModalIsOpen={toggleShareModalIsOpen}
					resetLockingSystem={resetLockingSystem}
					locale={locale}
					lockingSystemId={lockingSystemId}
				/>
				<LockingSystemMatrixHeader />

				{showKeyColumns && (
					<>
						{keyComponentsUuids.map((keyComponentUuid, cellIndex) => {
							const keyComponent = componentsByUuid?.[keyComponentUuid]
							const charges = chargesByUuid?.[keyComponentUuid]

							return (
								<LockingSystemKey
									key={keyComponent.uuid}
									isLoading={isFrozen || isLoading}
									isFrozen={isFrozen}
									keyComponent={keyComponent}
									charges={charges}
									cellIndex={cellIndex}
									removeKey={removeKey}
									duplicateKey={duplicateKey}
									setComponentName={setComponentName}
									setComponentQuantity={setComponentQuantity}
									setEditComponentModalUuid={setEditComponentModalUuid}
									moveMatrixComponent={moveMatrixComponent}
									toggleKeyComponentSelection={toggleKeyComponentSelection}
								/>
							)
						})}
					</>
				)}

				{lockingComponentsUuids.map((lockingComponentsUuid, rowIndex) => {
					const lockingComponent = componentsByUuid?.[lockingComponentsUuid] ?? null
					const charges = chargesByUuid?.[lockingComponentsUuid]

					return (
						<LockingSystemRow
							key={rowIndex}
							isLoading={isFrozen || isLoading}
							isFrozen={isFrozen}
							lockingSystemType={lockingSystemType}
							rowIndex={rowIndex}
							lockingComponent={lockingComponent}
							charges={charges}
							matrix={matrix}
							availableLockingComponents={availableLockingComponents}
							setCellValue={setCellValue}
							removeLock={removeLock}
							duplicateLock={duplicateLock}
							setComponentName={setComponentName}
							setComponentLockingSystemProduct={setComponentLockingSystemProduct}
							setComponentOuterLength={setComponentOuterLength}
							setComponentInnerLength={setComponentInnerLength}
							setEditComponentModalUuid={setEditComponentModalUuid}
							moveMatrixComponent={moveMatrixComponent}
							toggleLockingComponentSelections={toggleLockingComponentSelections}
						/>
					)
				})}
			</div>
		</>
	)
}

interface Props {
	isFrozen?: boolean
	isLoading?: boolean
	lockingSystemCategory: LockingSystemCategory
	matrix: LockingSystemReducerState["matrix"]
	matrixType: LockingSystemReducerState["matrixType"]
	lockingComponentsUuids: LockingSystemReducerState["lockingComponentsUuids"]
	keyComponentsUuids: LockingSystemReducerState["keyComponentsUuids"]
	componentsByUuid: LockingSystemReducerState["componentsByUuid"]
	charges: LockingSystemReducerState["charges"]
	availableLockingComponents?: LockingSystemPreviewProduct[]
	lockingSystemId: string
	locale: string
	addDefaultLockingComponent?: () => void
	addDefaultKeyComponent?: () => void
	setCellValue?: (rowIndex: number, cellIndex: number, isSelected: boolean) => void
	removeLock?: (uuid: string) => void
	removeKey?: (uuid: string) => void
	duplicateLock?: (uuid: string) => void
	duplicateKey?: (uuid: string) => void
	setComponentName?: (uuid: string, name: string) => void
	setComponentQuantity?: (uuid: string, quantity: number) => void
	setComponentLockingSystemProduct?: (
		uuid: string,
		lockingSystemPreviewProduct: LockingSystemPreviewProduct,
	) => void
	setComponentOuterLength?: (uuid: string, selectedOuterLength: number) => void
	setComponentInnerLength?: (uuid: string, selectedOuterLength: number) => void
	setEditComponentModalUuid?: (uuid: string) => void
	moveMatrixComponent?: (uuid: string, direction: MoveDirection) => void
	setSpecialMatrix?: (specialMatrixType: SpecialMatrixType) => void
	saveLockingSystem?: () => void
	duplicateLockingSystem?: () => void
	toggleShareModalIsOpen?: () => void
	resetLockingSystem?: () => void
	toggleLockingComponentSelections?: (uuid: string) => void
	toggleKeyComponentSelection?: (uuid: string) => void
}

export default LockingSystemMatrix
